import React, { Fragment, useCallback, useEffect, useState } from "react";
import { Container, Modal, ModalHeader, ModalBody } from "reactstrap";
import Breadcrumb from "../common/breadcrumb";
import { RefreshCw, AlertTriangle } from "react-feather";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { RCard, Loader as RLoader } from "../reporting/ReportingShared";
import ToggleSwitch from "../../shared/ToggleSwitch";
import { getScheduledJobs, setScheduledJobEnabled, getJobRuns } from "../../utils/apiCalls";
import { successToast, errorToast } from "../../shared/Toast";

// What the ~36 background jobs are actually doing.
//
// The page exists because until now nothing recorded that a job had run. Most of them only log when
// they found work, so one that stopped firing months ago looked exactly like a quiet one. Every
// column here answers a question that previously required reading server stdout.

const RUNS_PAGE_SIZE = 25;
// The data is a live status board, so it goes stale while you look at it.
const AUTO_REFRESH_MS = 30000;

const secondaryBtn = {
  background: C.white,
  border: `1px solid ${C.border}`,
  color: C.text,
  borderRadius: 10,
  fontWeight: 600,
  fontSize: 13,
  padding: "8px 14px",
  cursor: "pointer",
};
const inputStyle = {
  padding: "8px 12px",
  fontSize: 13,
  borderRadius: 8,
  border: `1.5px solid ${C.border}`,
  outline: "none",
  color: C.text,
  background: C.white,
};

const formatTime = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "—";
  const day = d.getDate();
  const month = d.toLocaleString("en-US", { month: "short" });
  let h = d.getHours();
  const m = d.getMinutes().toString().padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${day} ${month}, ${h}:${m} ${ampm}`;
};

/** "3 min ago" reads faster than a timestamp when the question is "is this thing alive". */
const formatAgo = (v) => {
  if (!v) return "never";
  const secs = Math.round((Date.now() - new Date(v).getTime()) / 1000);
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.round(secs / 60)} min ago`;
  if (secs < 86400) return `${Math.round(secs / 3600)} h ago`;
  return `${Math.round(secs / 86400)} d ago`;
};

const formatDuration = (ms) => {
  if (ms === null || ms === undefined) return "—";
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)} s`;
  return `${Math.round(ms / 60000)} min`;
};

const formatGap = (secs) => {
  if (!secs) return "—";
  if (secs < 3600) return `every ${Math.round(secs / 60)} min`;
  if (secs < 86400) return `every ${Math.round(secs / 3600)} h`;
  return `every ${Math.round(secs / 86400)} d`;
};

const Pill = ({ tone, children }) => {
  const tones = {
    good: { bg: C.greenLight, fg: C.green },
    bad: { bg: "#FDECEC", fg: "#dc3545" },
    warn: { bg: "#FFF4E5", fg: "#B26B00" },
    idle: { bg: C.borderLight, fg: C.textMed },
  };
  const t = tones[tone] || tones.idle;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "3px 9px",
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 700,
        background: t.bg,
        color: t.fg,
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: t.fg }} />
      {children}
    </span>
  );
};

// "disabled" is deliberately neutral rather than red: somebody chose it, and colouring a decision as
// a fault teaches people to ignore the colour that means something is actually broken.
const isPlainObject = (v) => v !== null && typeof v === "object" && !Array.isArray(v);
const isRecordList = (v) => Array.isArray(v) && v.length > 0 && v.every(isPlainObject);

/**
 * One line for a table cell. Numbers and short strings show as they are; a list shows its size and
 * an object its key count, so a job that reports 200 orders never floods the row. The full value
 * is one click away in DetailsModal.
 */
const compactSummary = (summary) =>
  Object.entries(summary || {})
    .map(([k, v]) => {
      if (Array.isArray(v)) return `${k}: ${v.length} item${v.length === 1 ? "" : "s"}`;
      if (isPlainObject(v)) return `${k}: {${Object.keys(v).length} fields}`;
      const text = String(v);
      return `${k}: ${text.length > 60 ? `${text.slice(0, 60)}…` : text}`;
    })
    .join(" · ");

/** True when the summary holds something the one-liner had to shorten. */
const summaryHasMore = (summary) =>
  Object.values(summary || {}).some((v) => (v !== null && typeof v === "object") || String(v).length > 60);

const preStyle = {
  margin: 0,
  maxHeight: 360,
  overflow: "auto",
  background: C.bg,
  padding: 12,
  borderRadius: 8,
  fontSize: 11,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
};

/** A list of records (e.g. the orders a job flagged) as a table: one column per key seen. */
const RecordTable = ({ rows }) => {
  const columns = [...new Set(rows.flatMap((r) => Object.keys(r)))];
  const cell = (v) => {
    if (v === null || v === undefined) return <span style={{ color: C.textLight }}>—</span>;
    if (typeof v === "object") return Array.isArray(v) && v.every((x) => typeof x !== "object") ? v.join(", ") : JSON.stringify(v);
    return String(v);
  };
  return (
    <div style={{ overflow: "auto", maxHeight: 420, border: `1px solid ${C.border}`, borderRadius: 8 }}>
      <table style={{ ...tbl.table, fontSize: 11 }}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c} style={{ ...tbl.th, position: "sticky", top: 0, background: C.bg, whiteSpace: "nowrap", zIndex: 1 }}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id ?? i}>
              {columns.map((c) => (
                <td key={c} style={{ ...tbl.td, whiteSpace: "nowrap", fontSize: 11 }}>{cell(r[c])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/** Everything a run (or a job's last run) reported, in full. */
const DetailsModal = ({ details, onClose }) => {
  const summary = details?.summary || null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify({ error: details?.error || undefined, summary: summary || undefined }, null, 2));
      successToast("Copied");
    } catch (e) {
      errorToast("Could not copy");
    }
  };

  return (
    <Modal isOpen={Boolean(details)} toggle={onClose} size="xl">
      <ModalHeader toggle={onClose}>
        {details?.jobName}
        {details?.when && <span style={{ fontSize: 12, color: C.textLight, fontWeight: 400, marginLeft: 10 }}>{formatTime(details.when)}</span>}
      </ModalHeader>
      <ModalBody>
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
          <button type="button" style={{ ...secondaryBtn, padding: "6px 12px", fontSize: 12 }} onClick={copy}>Copy JSON</button>
        </div>

        {details?.error && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#dc3545", marginBottom: 6 }}>Error</div>
            <pre style={{ ...preStyle, color: "#7f1d1d", background: "#FDECEC" }}>{details.error}</pre>
          </div>
        )}

        {summary && Object.entries(summary).map(([key, value]) => (
          <div key={key} style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 6 }}>
              {key}
              {Array.isArray(value) && <span style={{ color: C.textLight, fontWeight: 400 }}> — {value.length} item{value.length === 1 ? "" : "s"}</span>}
            </div>
            {isRecordList(value) ? (
              <RecordTable rows={value} />
            ) : value !== null && typeof value === "object" ? (
              <pre style={preStyle}>{JSON.stringify(value, null, 2)}</pre>
            ) : (
              <div style={{ fontSize: 13, color: C.textMed, wordBreak: "break-word" }}>{String(value)}</div>
            )}
          </div>
        ))}

        {!details?.error && !summary && <div style={{ color: C.textLight }}>Nothing was reported.</div>}
      </ModalBody>
    </Modal>
  );
};

const detailsBtn = { ...secondaryBtn, padding: "3px 10px", fontSize: 11, marginTop: 6 };

const HEALTH = {
  ok: { tone: "good", label: "Running" },
  late: { tone: "bad", label: "Not running" },
  failing: { tone: "bad", label: "Failing" },
  pending: { tone: "idle", label: "Waiting" },
  disabled: { tone: "idle", label: "Disabled" },
};

const CronJobs = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyJob, setBusyJob] = useState(null);

  const [runs, setRuns] = useState([]);
  const [runsTotal, setRunsTotal] = useState(0);
  const [runsPage, setRunsPage] = useState(1);
  const [runsJob, setRunsJob] = useState("");
  const [runsLoading, setRunsLoading] = useState(false);
  const [details, setDetails] = useState(null); // { jobName, when, error, summary }

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    const result = await getScheduledJobs();
    if (result) setData(result);
    if (!quiet) setLoading(false);
  }, []);

  const loadRuns = useCallback(async (page, jobName) => {
    setRunsLoading(true);
    const params = { page, limit: RUNS_PAGE_SIZE };
    if (jobName) params.jobName = jobName;
    const result = await getJobRuns(params);
    if (result) {
      setRuns(result.runs || []);
      setRunsTotal(result.total || 0);
    }
    setRunsLoading(false);
  }, []);

  useEffect(() => {
    load();
    // Quiet refreshes: a spinner every 30 seconds would make a healthy page look busy.
    const timer = setInterval(() => load({ quiet: true }), AUTO_REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  useEffect(() => {
    loadRuns(runsPage, runsJob);
  }, [loadRuns, runsPage, runsJob]);

  const handleToggle = async (job, next) => {
    if (!next) {
      const ok = window.confirm(
        `Disable ${job.jobName}?\n\nIt will be skipped from its next tick on every server, until someone turns it back on. Payment reconcilers and order cleanup jobs run on this list — make sure you know what this one does.`
      );
      if (!ok) return;
    }
    setBusyJob(job.jobName);
    await setScheduledJobEnabled(job.jobName, next);
    setBusyJob(null);
    load({ quiet: true });
  };

  const jobs = data?.jobs || [];
  const counts = jobs.reduce((acc, j) => {
    const state = j.health?.state || "pending";
    acc[state] = (acc[state] || 0) + 1;
    return acc;
  }, {});
  const broken = (counts.late || 0) + (counts.failing || 0);
  const runsTotalPages = Math.max(1, Math.ceil(runsTotal / RUNS_PAGE_SIZE));

  return (
    <Fragment>
      <Breadcrumb title="Scheduled Jobs" parent="System" />
      <Container fluid>
        <style>{reportingGlobalCSS}</style>
        <div className="rpt-wrap">
          <RCard>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 12,
                marginBottom: broken > 0 ? 14 : 0,
              }}
            >
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                <span style={{ fontSize: 16, fontWeight: 700, color: C.text }}>{jobs.length} jobs</span>
                {counts.ok > 0 && <Pill tone="good">{counts.ok} running</Pill>}
                {counts.late > 0 && <Pill tone="bad">{counts.late} not running</Pill>}
                {counts.failing > 0 && <Pill tone="bad">{counts.failing} failing</Pill>}
                {counts.pending > 0 && <Pill tone="idle">{counts.pending} waiting</Pill>}
                {counts.disabled > 0 && <Pill tone="idle">{counts.disabled} disabled</Pill>}
              </div>
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <span style={{ fontSize: 11, color: C.textLight }}>
                  server up {formatGap(data?.serverUptimeSeconds).replace("every ", "")}
                </span>
                <button style={secondaryBtn} onClick={() => load()}>
                  <RefreshCw size={13} /> Refresh
                </button>
              </div>
            </div>

            {broken > 0 && (
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  padding: "10px 12px",
                  borderRadius: 8,
                  background: "#FDECEC",
                  color: "#dc3545",
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                <AlertTriangle size={15} />
                {broken} job(s) are not doing their work. An email goes out for each one at most once
                every few hours — this page is the full picture.
              </div>
            )}
          </RCard>

          <RCard>
            {loading && !data ? (
              <RLoader />
            ) : (
              <div style={{ overflowX: "auto", borderRadius: 10, border: `1px solid ${C.border}` }}>
                <table style={tbl.table}>
                  <thead>
                    <tr>
                      <th style={tbl.th}>Job</th>
                      <th style={tbl.th}>Schedule</th>
                      <th style={tbl.th}>Status</th>
                      <th style={tbl.th}>Last run</th>
                      <th style={tbl.th}>Took</th>
                      <th style={tbl.th}>Next run</th>
                      <th style={tbl.th}>Runs</th>
                      <th style={{ ...tbl.th, textAlign: "center" }}>On</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.length === 0 ? (
                      <tr>
                        <td colSpan="8" style={{ ...tbl.td, textAlign: "center", padding: 40, color: C.textLight }}>
                          No scheduled jobs registered. The server may not have been restarted since
                          this page was added.
                        </td>
                      </tr>
                    ) : (
                      jobs.map((job) => {
                        const health = HEALTH[job.health?.state] || HEALTH.pending;
                        return (
                          <tr key={job.jobName} className="rpt-row">
                            <td style={{ ...tbl.td, fontWeight: 700, color: C.text }}>
                              {job.jobName}
                              {job.lastError && (
                                <div
                                  style={{
                                    fontSize: 11,
                                    color: "#dc3545",
                                    fontWeight: 400,
                                    marginTop: 4,
                                    maxWidth: 380,
                                    whiteSpace: "pre-wrap",
                                  }}
                                >
                                  {String(job.lastError).split("\n")[0]}
                                </div>
                              )}
                              {job.lastSummary && (
                                <div style={{ fontSize: 11, color: C.textLight, fontWeight: 400, marginTop: 3 }}>
                                  {compactSummary(job.lastSummary)}
                                </div>
                              )}
                              {(job.lastError || summaryHasMore(job.lastSummary)) && (
                                <button
                                  type="button"
                                  style={detailsBtn}
                                  onClick={() => setDetails({ jobName: job.jobName, when: job.lastFinishedAt, error: job.lastError, summary: job.lastSummary })}
                                >
                                  Details
                                </button>
                              )}
                            </td>
                            <td style={{ ...tbl.td, whiteSpace: "nowrap" }}>
                              <span style={{ fontFamily: "monospace", fontSize: 11 }}>{job.cronExpression}</span>
                              <div style={{ fontSize: 11, color: C.textLight }}>{formatGap(job.maxGapSeconds)}</div>
                            </td>
                            <td style={tbl.td}>
                              <Pill tone={health.tone}>{health.label}</Pill>
                              {job.health?.detail && (
                                <div style={{ fontSize: 11, color: C.textLight, marginTop: 4 }}>{job.health.detail}</div>
                              )}
                              {!job.enabled && job.disabledBy && (
                                <div style={{ fontSize: 11, color: C.textLight, marginTop: 4 }}>
                                  by {job.disabledBy}
                                </div>
                              )}
                            </td>
                            <td style={{ ...tbl.td, whiteSpace: "nowrap" }}>
                              {formatAgo(job.lastFinishedAt)}
                              <div style={{ fontSize: 11, color: C.textLight }}>{formatTime(job.lastFinishedAt)}</div>
                            </td>
                            <td style={{ ...tbl.td, whiteSpace: "nowrap" }}>{formatDuration(job.lastDurationMs)}</td>
                            <td style={{ ...tbl.td, whiteSpace: "nowrap", color: job.enabled ? C.textMed : C.textLight }}>
                              {job.enabled ? formatTime(job.nextRunAt) : "—"}
                            </td>
                            <td style={tbl.td}>
                              {job.totalRuns}
                              {job.consecutiveFailures > 0 && (
                                <div style={{ fontSize: 11, color: "#dc3545" }}>
                                  {job.consecutiveFailures} failing
                                </div>
                              )}
                            </td>
                            <td style={{ ...tbl.td, textAlign: "center" }}>
                              <div style={{ display: "flex", justifyContent: "center", opacity: busyJob === job.jobName ? 0.5 : 1 }}>
                                <ToggleSwitch
                                  checked={!!job.enabled}
                                  handleToggle={(v) => handleToggle(job, v)}
                                />
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </RCard>

          {/* Only runs that failed, dragged, or reported work are stored — a row per tick would be
              thousands a day of "a cleanup job found nothing". */}
          <RCard>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 14,
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>Run history</div>
                <div style={{ fontSize: 12, color: C.textLight, marginTop: 2 }}>
                  Failures, slow runs, and runs that reported work
                </div>
              </div>
              <select
                style={{ ...inputStyle, width: "auto" }}
                value={runsJob}
                onChange={(e) => {
                  setRunsPage(1);
                  setRunsJob(e.target.value);
                }}
              >
                <option value="">All jobs</option>
                {jobs.map((j) => (
                  <option key={j.jobName} value={j.jobName}>
                    {j.jobName}
                  </option>
                ))}
              </select>
            </div>

            {runsLoading ? (
              <RLoader />
            ) : (
              <>
                <div style={{ overflowX: "auto", borderRadius: 10, border: `1px solid ${C.border}` }}>
                  <table style={tbl.table}>
                    <thead>
                      <tr>
                        <th style={tbl.th}>When</th>
                        <th style={tbl.th}>Job</th>
                        <th style={tbl.th}>Outcome</th>
                        <th style={tbl.th}>Took</th>
                        <th style={tbl.th}>Detail</th>
                      </tr>
                    </thead>
                    <tbody>
                      {runs.length === 0 ? (
                        <tr>
                          <td colSpan="5" style={{ ...tbl.td, textAlign: "center", padding: 40, color: C.textLight }}>
                            Nothing recorded yet — which here means no job has failed, dragged, or
                            reported work.
                          </td>
                        </tr>
                      ) : (
                        runs.map((run) => (
                          <tr key={run.id} className="rpt-row">
                            <td style={{ ...tbl.td, whiteSpace: "nowrap" }}>{formatTime(run.startedAt)}</td>
                            <td style={{ ...tbl.td, fontWeight: 600 }}>{run.jobName}</td>
                            <td style={tbl.td}>
                              <Pill tone={run.status === "failed" ? "bad" : run.reason === "slow" ? "warn" : "good"}>
                                {run.reason}
                              </Pill>
                            </td>
                            <td style={{ ...tbl.td, whiteSpace: "nowrap" }}>{formatDuration(run.durationMs)}</td>
                            <td style={tbl.td}>
                              {run.error || run.summary ? (
                                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                                  <span style={{ fontSize: 12, color: run.error ? "#dc3545" : C.textMed, maxWidth: 420, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {run.error ? String(run.error).split("\n")[0] : compactSummary(run.summary)}
                                  </span>
                                  {(run.error || summaryHasMore(run.summary)) && (
                                    <button
                                      type="button"
                                      style={{ ...detailsBtn, marginTop: 0 }}
                                      onClick={() => setDetails({ jobName: run.jobName, when: run.startedAt, error: run.error, summary: run.summary })}
                                    >
                                      Details
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <span style={{ color: C.textLight }}>—</span>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {runsTotal > RUNS_PAGE_SIZE && (
                  <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, alignItems: "center", marginTop: 12 }}>
                    <button
                      style={{ ...secondaryBtn, opacity: runsPage <= 1 ? 0.5 : 1 }}
                      disabled={runsPage <= 1}
                      onClick={() => setRunsPage((p) => Math.max(1, p - 1))}
                    >
                      Previous
                    </button>
                    <span style={{ fontSize: 12, color: C.textMed }}>
                      {runsPage} / {runsTotalPages}
                    </span>
                    <button
                      style={{ ...secondaryBtn, opacity: runsPage >= runsTotalPages ? 0.5 : 1 }}
                      disabled={runsPage >= runsTotalPages}
                      onClick={() => setRunsPage((p) => Math.min(runsTotalPages, p + 1))}
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </RCard>
        </div>
      </Container>

      <DetailsModal details={details} onClose={() => setDetails(null)} />
    </Fragment>
  );
};

export default CronJobs;
